import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Loader2, LogOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PricingPlan {
  id: 'monthly' | 'annual';
  name: string;
  price: number;
  billingPeriod: string;
  features: string[];
  badge?: string;
}

const plans: PricingPlan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 130,
    billingPeriod: 'per month',
    features: [
      'Full access to player database',
      'Advanced search and filters',
      'Email integration',
      'Unlimited shortlists',
      'Export capabilities'
    ]
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 100,
    billingPeriod: 'per month',
    badge: 'Save 23%',
    features: [
      'Everything in Monthly plan',
      'Priority support',
      'Early access to new features',
      'Annual performance reports',
      'Save $360 per year'
    ]
  }
];

export const PaymentModal: React.FC<PaymentModalProps> = ({ open, onOpenChange }) => {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user, logout } = useAuth();

  const handleSubscribe = async () => {
    setIsLoading(true);
    
    try {
      // Add localhost-specific debugging
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        console.log('🔧 Localhost Debug - Payment Request:', {
          selectedPlan,
          apiBaseURL: api.defaults.baseURL,
          hasSessionToken: !!localStorage.getItem('sessionId')
        });
        
        // Test backend health first
        try {
          const healthResponse = await api.get('/api/payments/health');
          console.log('🔧 Localhost Debug - Backend Health:', healthResponse.data);
        } catch (healthError) {
          console.log('🔧 Localhost Debug - Health Check Failed:', healthError);
        }
      }

      // Create a checkout session
      const response = await api.post('/api/payments/create-checkout-session', {
        price_type: selectedPlan
      });

      if (isLocalhost) {
        console.log('🔧 Localhost Debug - Payment Response:', response);
      }

      if (response.data.checkout_url) {
        // Set payment in progress flag
        sessionStorage.setItem('paymentInProgress', 'true');
        
        // Store session info for verification
        sessionStorage.setItem('checkoutSession', JSON.stringify({
          sessionId: response.data.session_id,
          userId: user?.id,
          timestamp: Date.now()
        }));
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      
      // Enhanced error handling for localhost
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      let errorMessage = 'Failed to start checkout process. Please try again.';
      
      if (isLocalhost) {
        // More detailed error for localhost debugging
        if (error.response) {
          console.log('🔧 Localhost Debug - Error Response:', {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          });
          
          if (error.response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (error.response.status === 500) {
            errorMessage = `Server error (${error.response.status}). Check backend logs.`;
          } else {
            errorMessage = `Request failed (${error.response.status}): ${error.response.data?.detail || 'Unknown error'}`;
          }
        } else if (error.request) {
          console.log('🔧 Localhost Debug - No Response:', error.request);
          errorMessage = 'No response from server. Is the backend running on localhost:8000?';
        } else {
          console.log('🔧 Localhost Debug - Request Setup Error:', error.message);
          errorMessage = `Request setup error: ${error.message}`;
        }
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl p-0 [&>button]:hidden w-[95vw] max-h-[95vh] overflow-y-auto" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-6 pb-0">
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-2xl font-bold">Choose Your Plan</DialogTitle>
              <DialogDescription className="mt-1">
                Select a subscription plan to access all features of Bloom Soccer Scouting
              </DialogDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-gray-600 hover:text-red-600"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </DialogHeader>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative cursor-pointer transition-all ${
                  selectedPlan === plan.id
                    ? 'ring-2 ring-primary shadow-lg'
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.badge && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-muted-foreground ml-1">{plan.billingPeriod}</span>
                    {plan.id === 'annual' && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Billed annually at $1200
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              size="lg"
              onClick={handleSubscribe}
              disabled={isLoading}
              className="min-w-[200px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Subscribe to ${selectedPlan === 'annual' ? 'Annual' : 'Monthly'} Plan`
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            You will be redirected to our secure payment processor. Cancel anytime from your account settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
